import { Component, HostBinding, HostListener, Inject } from '@angular/core';
import { UserTokenService } from '../services/user-service/user-token.service';
import { LoginHelper } from '../helper/login.helper';
import { PROFILE_DATA_SERVICE, ProfileDataService } from '../services/profile/profile-data.service';
import { UserProfileShareService } from '../services/shared-service/user-profile-share.service';
import { Router } from '@angular/router';
import { Observable } from "rxjs";
import { Select } from "@ngxs/store";
import { LoaderState } from "./store/loader/loader.state";
import { CLOUD_DATA_SERVICE, CloudDataService } from '../services/cloud/cloud-data.service';
import { ProfileData } from '../models/profile/profile.model';
import { productFruits } from 'product-fruits';
import { CONTACTS_HAS_READ_RIGHTS } from '../constants/constants';
import { environment } from '../environments/environment';
import { hashUserId } from '../helper/crypto.helper';
import { OverlayContainer } from '@angular/cdk/overlay';

@Component({
  selector: 'fi-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
  standalone: false,
})
export class AppComponent {
  @HostBinding('attr.data-theme') responsiveTokenTheme = this.getResponsiveTokenTheme();

  @Select(LoaderState.loading) loading$: Observable<boolean>;

  static maintenanceMode = false;

  isAuthorized = false;
  isSuccess = false;
  message: string;

  constructor( private readonly userTokenService: UserTokenService,
               private readonly router: Router,
               @Inject(PROFILE_DATA_SERVICE) private readonly profileDataService: ProfileDataService,
               @Inject(CLOUD_DATA_SERVICE) public cloudDataService: CloudDataService,
               private readonly sharedProfileService: UserProfileShareService,
               private readonly overlayContainer: OverlayContainer
  ) {

    this.applyResponsiveTokenTheme();

    const url = new URL(window.location.href);
    const token = url.searchParams.get('token');
    this.message = url.searchParams.get('message');
    this.isSuccess = url.searchParams.get('success') !== '0' && url.searchParams.get('success') !== 'false' && !this.message;

    if ( token && this.isSuccess ) {
      userTokenService.setToken(token);
      const path = localStorage.getItem('lastOpenedPath') ?? '/';
      localStorage.removeItem('lastOpenedPath');

      // Replace rather than push, so the redirect URL carrying ?token= does not
      // stay in session history once the token has been consumed.
      router.navigateByUrl(path, { replaceUrl: true });
    }

    const gotTokenSuccessfully = !!userTokenService.getToken() && this.isSuccess;

    if ( !gotTokenSuccessfully && !AppComponent.maintenanceMode ) {
      this.attemptToLogin();
    } else if ( this.isSuccess ) {
      cloudDataService.getAdminMandants().subscribe(( mandantData ) => {
        if ( mandantData.success ) {
          this.sharedProfileService.setAdminMandant(mandantData.data[0]);
        }
      });

      this.profileDataService.getUserProfile().subscribe(( { data, success } ) => {
        if ( success ) {
          if ( !data.Rights.includes(CONTACTS_HAS_READ_RIGHTS) ) {
            window.location.href = environment.cloudUrl;
            this.userTokenService.removeStorage();
            this.isAuthorized = false;
            return;
          }
          if ( !data.User.LastActivity ) {
            document.body.classList.add('is_first-time-login');
            this.router.navigate(['/']);
          }
          const languageCode = data.User.LanguageCode.toLowerCase();
          document.body.setAttribute('data-lang', languageCode);
          this.profileDataService.getSubordinates().subscribe(( subordinates ) => {
            if ( subordinates.success ) {
              data.Subordinates = subordinates.data;
            }
            this.sharedProfileService.setProfile(data);
            this.isAuthorized = true;
          })
          this.setProductFruitsData(data, languageCode)
        }
      });
    }
  }

  private setProductFruitsData( profile: ProfileData, languageCode: string ) {
    const tenants = profile?.Tenants ?? [];
    const isDealerPartner = this.sharedProfileService.computeIsDealerPartner(tenants);
    productFruits.init(
      environment.productFruitsWorkspace,
      languageCode,
      {
        username: hashUserId(profile?.User.Id),
        role: isDealerPartner ? 'dealer' : '',
      },
      {
        disableLocationChangeDetection: false
      }
    );

    productFruits.safeExec((_) => {
      const id = environment.productFruitsChecklistId;
      if (isDealerPartner && id) {
        const storageKey = 'productFruitsLastChecklistReset';
        const oneDay = 24 * 60 * 60 * 1000;
        const twoWeeksMs = 14 * oneDay;
        const lastReset = localStorage.getItem(storageKey);
        const now = Date.now();
        const api = (window.productFruits as any).api;
        const checkLists = api.checklists.getChecklists();
        const item = checkLists.find((it) => it.id === id);
        if (item && item.state === 'finished') {
          api.checklists.resetForCurrentUser(id);
        }
        if (!lastReset || now - parseInt(lastReset, 10) >= twoWeeksMs) {
          api.checklists.resetForCurrentUser(id)
          localStorage.setItem(storageKey, String(now));
        }
      }
    });

  }

  attemptToLogin(): void {
    if ( !localStorage.getItem('lastOpenedPath') ) {
      localStorage.setItem('lastOpenedPath', window.location.pathname ? window.location.pathname : '/');
    }

    LoginHelper.forgetTokenAndGoToLoginPage(this.userTokenService);
  }

  @HostListener('window:resize')
  updateResponsiveTokenTheme(): void {
    this.responsiveTokenTheme = this.getResponsiveTokenTheme();
    this.applyResponsiveTokenTheme();
  }

  private applyResponsiveTokenTheme(): void {
    this.overlayContainer.getContainerElement().setAttribute('data-theme', this.responsiveTokenTheme);
  }

  private getResponsiveTokenTheme(): 'Mobile' | 'Tablet' | 'Desktop' {
    if ( window.innerWidth < 768 ) {
      return 'Mobile';
    }

    if ( window.innerWidth < 1024 ) {
      return 'Tablet';
    }

    return 'Desktop';
  }
}
